// ABOUTME: Full-screen celebration overlay for level-up moments
// ABOUTME: Features badge animation, particle burst, and auto-dismiss

import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect } from 'react'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { duration, easing } from '@/lib/utils/animations'

interface LevelUpCelebrationProps {
  level: number
  isActive: boolean
  onComplete: () => void
  xpGained?: number
  autoDismissMs?: number
}

export function LevelUpCelebration({
  level,
  isActive,
  onComplete,
  xpGained,
  autoDismissMs = 4000,
}: LevelUpCelebrationProps) {
  const prefersReducedMotion = useReducedMotion()

  // Auto-dismiss timer
  useEffect(() => {
    if (!isActive) return

    const timer = setTimeout(onComplete, autoDismissMs)
    return () => clearTimeout(timer)
  }, [isActive, onComplete, autoDismissMs])

  const handleDismiss = useCallback(() => {
    onComplete()
  }, [onComplete])

  if (!isActive) return null

  const badgeVariants = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 } }
    : {
        initial: { opacity: 0, scale: 0 },
        animate: {
          opacity: 1,
          scale: 1,
          transition: {
            duration: duration.bounce,
            ease: easing.bounce,
          },
        },
      }

  const textVariants = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 } }
    : {
        initial: { opacity: 0, y: 20 },
        animate: {
          opacity: 1,
          y: 0,
          transition: {
            delay: 0.3,
            duration: duration.enter,
            ease: easing.enter,
          },
        },
      }

  return (
    <AnimatePresence>
      <motion.button
        type="button"
        className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleDismiss}
        aria-label="Dismiss celebration"
      >
        {/* Particle Burst */}
        {!prefersReducedMotion && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="particle-burst" />
          </div>
        )}

        {/* Level Badge */}
        <motion.div
          className="relative flex flex-col items-center"
          variants={badgeVariants}
          initial="initial"
          animate="animate"
        >
          <div className="w-32 h-32 rounded-full bg-editorial-gold flex items-center justify-center shadow-2xl">
            <span className="text-5xl font-bold text-white uppercase tracking-wider">{level}</span>
          </div>

          <motion.div
            className="mt-6 text-center"
            variants={textVariants}
            initial="initial"
            animate="animate"
          >
            <h2 className="text-3xl font-bold text-white uppercase tracking-wider">
              LEVEL {level}
            </h2>
            <p className="mt-2 text-lg text-white/80">Congratulations!</p>
          </motion.div>

          {xpGained && (
            <motion.div
              className="mt-4 px-4 py-2 rounded-full bg-white/10"
              variants={textVariants}
              initial="initial"
              animate="animate"
            >
              <span className="text-[var(--color-xp)] font-semibold">
                +{xpGained.toLocaleString()} XP
              </span>
            </motion.div>
          )}
        </motion.div>

        <motion.p
          className="absolute bottom-8 text-white/60 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 1.5 } }}
        >
          Tap anywhere to continue
        </motion.p>
      </motion.button>
    </AnimatePresence>
  )
}
